// Pure tab logic for the reader. No DOM, no React — every function takes a
// state `{ tabs, activeIndex }` and returns a new one (or a derived value).
//
// tab  : { id, path, scrollTop, hist: { stack: string[], index } }
// state: { tabs: Tab[], activeIndex }   (activeIndex -1 only when tabs is empty)
//
// `path` is null for a blank tab ("Select a file to begin"). `id` is supplied
// by the caller (an incrementing counter) so this module stays deterministic
// and testable — it never calls Date.now()/Math.random().
//
// History model: each tab owns its own back/forward stack. Plain navigation
// (sidebar click, in-doc link) replaces the active tab's file in place and
// pushes onto that tab's history — the browser-tab model.

export function createTab(path, id, scrollTop = 0) {
  return path == null
    ? { id, path: null, scrollTop, hist: { stack: [], index: -1 } }
    : { id, path, scrollTop, hist: { stack: [path], index: 0 } };
}

export function findTabIndex(tabs, path) {
  for (let i = 0; i < tabs.length; i++) {
    if (tabs[i].path === path) return i;
  }
  return -1;
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

export function openOrActivate(state, path, id) {
  const existing = findTabIndex(state.tabs, path);
  if (existing >= 0) return { tabs: state.tabs, activeIndex: existing };
  const at = state.activeIndex;
  const tab = createTab(path, id);
  const tabs = [...state.tabs.slice(0, at + 1), tab, ...state.tabs.slice(at + 1)];
  return { tabs, activeIndex: at + 1 };
}

// Re-insert a previously-closed tab — with its saved scroll and back/forward
// history — after the active tab, and focus it. `closed` is { path, scrollTop,
// hist }. If a tab for the path is already open, just activate that one.
export function reopenTab(state, closed, id) {
  const existing = findTabIndex(state.tabs, closed.path);
  if (existing >= 0) return { tabs: state.tabs, activeIndex: existing };
  const hasHist = closed.hist && Array.isArray(closed.hist.stack) && closed.hist.stack.length > 0;
  const hist = hasHist
    ? { stack: closed.hist.stack.slice(), index: clamp(closed.hist.index, 0, closed.hist.stack.length - 1) }
    : { stack: [closed.path], index: 0 };
  const tab = { id, path: closed.path, scrollTop: closed.scrollTop ?? 0, hist };
  const at = state.activeIndex;
  const tabs = [...state.tabs.slice(0, at + 1), tab, ...state.tabs.slice(at + 1)];
  return { tabs, activeIndex: at + 1 };
}

export function navigateActive(state, path, id) {
  if (state.activeIndex < 0 || state.tabs.length === 0) {
    return openOrActivate(state, path, id);
  }
  const active = state.tabs[state.activeIndex];
  if (active.path === path) return state; // already showing it
  const other = findTabIndex(state.tabs, path);
  if (other >= 0 && other !== state.activeIndex) {
    return { tabs: state.tabs, activeIndex: other }; // dedupe: jump to it
  }
  const stack = [...active.hist.stack.slice(0, active.hist.index + 1), path];
  const nextTab = { ...active, path, hist: { stack, index: stack.length - 1 } };
  const tabs = state.tabs.map((t, i) => (i === state.activeIndex ? nextTab : t));
  return { tabs, activeIndex: state.activeIndex };
}

export function closeTab(state, index) {
  const tabs = state.tabs.filter((_, i) => i !== index);
  if (tabs.length === 0) return { tabs: [], activeIndex: -1 };
  let activeIndex;
  if (index === state.activeIndex) {
    activeIndex = Math.max(0, index - 1); // prefer the left neighbour
  } else if (index < state.activeIndex) {
    activeIndex = state.activeIndex - 1;
  } else {
    activeIndex = state.activeIndex;
  }
  return { tabs, activeIndex: clamp(activeIndex, 0, tabs.length - 1) };
}

export function switchTo(state, index) {
  return { tabs: state.tabs, activeIndex: clamp(index, 0, state.tabs.length - 1) };
}

// Remember a tab's scroll offset so it can be restored when the tab is shown
// again (after scrolling out of the carousel's live window, or after a reload).
export function setScroll(state, index, scrollTop) {
  if (index < 0 || index >= state.tabs.length) return state;
  if (state.tabs[index].scrollTop === scrollTop) return state;
  const tabs = state.tabs.map((t, i) => (i === index ? { ...t, scrollTop } : t));
  return { tabs, activeIndex: state.activeIndex };
}

function moveActive(state, delta) {
  const active = state.tabs[state.activeIndex];
  if (!active) return { state, path: null };
  const nextIdx = active.hist.index + delta;
  if (nextIdx < 0 || nextIdx > active.hist.stack.length - 1) return { state, path: null };
  const path = active.hist.stack[nextIdx];
  const nextTab = { ...active, path, hist: { ...active.hist, index: nextIdx } };
  const tabs = state.tabs.map((t, i) => (i === state.activeIndex ? nextTab : t));
  return { state: { tabs, activeIndex: state.activeIndex }, path };
}

export function backInActive(state) {
  return moveActive(state, -1);
}

export function forwardInActive(state) {
  return moveActive(state, +1);
}

// In-place rewrite of the active tab's path (rename / move fixup) — updates the
// current history entry rather than pushing a new one, so back/forward is not
// polluted by a rename.
export function setActivePath(state, path) {
  if (state.activeIndex < 0) return state;
  const a = state.tabs[state.activeIndex];
  const stack = a.hist.stack.slice();
  if (a.hist.index >= 0) stack[a.hist.index] = path;
  else stack.push(path);
  const index = a.hist.index >= 0 ? a.hist.index : stack.length - 1;
  const nt = { ...a, path, hist: { stack, index } };
  return { tabs: state.tabs.map((t, i) => (i === state.activeIndex ? nt : t)), activeIndex: state.activeIndex };
}

// Keep-alive window for desktop tab switching. To make a switch feel instant,
// the active tab plus the most-recently-active tabs stay mounted, so switching
// back to one is a CSS show/hide with no refetch or re-render. Tabs beyond the
// window are unmounted and remount (restoring their saved scroll) on next
// activation. Pure: maps the prior mounted set onto the new one.
//
// `prev`     : ordered mounted tab ids, most-recent first.
// `activeId` : the id that just became active (kept, moved to front).
// `openIds`  : ids of currently-open tabs; closed tabs are pruned.
// `max`      : window size, active included (floored at 1).
// Returns the new ordered list (most-recent first), capped at `max`. The active
// id is at index 0, so it is never the one evicted.
export function updateKeepAlive(prev, activeId, openIds, max) {
  const open = new Set(openIds);
  const k = Math.max(1, Math.floor(max) || 0);
  const pruned = prev.filter((id) => open.has(id) && id !== activeId);
  const ordered = open.has(activeId) ? [activeId, ...pruned] : pruned;
  return ordered.slice(0, k);
}

export function serializeTabs(state) {
  return { tabs: state.tabs.map((t) => ({ path: t.path, scrollTop: t.scrollTop })), activeIndex: state.activeIndex };
}

export function deserializeTabs(json, isOpenable) {
  if (!json || !Array.isArray(json.tabs)) return { tabs: [], activeIndex: -1 };
  const kept = json.tabs.filter(
    (e) => e && (e.path == null || (typeof e.path === 'string' && isOpenable(e.path))),
  );
  const tabs = kept.map((e, i) => createTab(e.path, i, Number.isFinite(e.scrollTop) && e.scrollTop >= 0 ? e.scrollTop : 0));
  if (tabs.length === 0) return { tabs: [], activeIndex: -1 };
  let activeIndex = typeof json.activeIndex === 'number' ? json.activeIndex : 0;
  activeIndex = clamp(activeIndex, 0, tabs.length - 1);
  return { tabs, activeIndex };
}
